import React from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { Tooltip } from '@mui/material';
import TabBar, { TabProps } from 'src/components/layouts/TabBar/TabBar';
import Icon from 'src/components/ui/Icon/Icon';
import { KpiDateRange } from 'src/services/kpi';
import { KpiDateBounds, presetIsVisible, rangeForPreset, RangePreset } from 'src/services/carrierKpi';

export interface AnalyticsRangeState {
  preset: RangePreset;
  monthAnchor: Date;
}

export const initialAnalyticsRange = (): AnalyticsRangeState => ({
  preset: 'last_30',
  monthAnchor: new Date(),
});

export const analyticsRangeToDates = (state: AnalyticsRangeState, today: Date = new Date()): KpiDateRange =>
  rangeForPreset(state.preset, today, state.monthAnchor);

const PRESETS: { key: RangePreset; labelKey: string }[] = [
  { key: 'yesterday', labelKey: 'carrier_analytics.preset_yesterday' },
  { key: 'last_7', labelKey: 'carrier_analytics.preset_7d' },
  { key: 'last_30', labelKey: 'carrier_analytics.preset_last_30' },
  { key: 'month', labelKey: 'carrier_analytics.preset_month' },
  { key: 'last_month', labelKey: 'carrier_analytics.preset_last_month' },
  { key: 'months_3', labelKey: 'carrier_analytics.preset_3m' },
  { key: 'months_6', labelKey: 'carrier_analytics.preset_6m' },
  { key: 'months_12', labelKey: 'carrier_analytics.preset_12m' },
  { key: 'year', labelKey: 'carrier_analytics.preset_year' },
  { key: 'total', labelKey: 'carrier_analytics.preset_total' },
];

const formatDisplayDate = (isoDate?: string): string => {
  if (!isoDate) {
    return '';
  }
  const [year, month, day] = isoDate.split('-');
  return `${day}.${month}.${year}`;
};

export interface CarrierAnalyticsToolbarProps {
  value: AnalyticsRangeState;
  onChange: (next: AnalyticsRangeState) => void;
  kpiDateBounds?: KpiDateBounds;
}

const CarrierAnalyticsToolbar: React.FC<CarrierAnalyticsToolbarProps> = ({ value, onChange, kpiDateBounds }) => {
  const { t, i18n } = useTranslation();

  const visiblePresets = PRESETS.filter(
    preset => preset.key === value.preset || presetIsVisible(preset.key, new Date(), kpiDateBounds),
  );

  const tabs: TabProps[] = visiblePresets.map(preset => ({
    label: t(preset.labelKey),
    key: preset.key,
    onClick: () => onChange({ ...value, preset: preset.key }),
  }));

  const monthIndex = (date: Date) => date.getFullYear() * 12 + date.getMonth();
  const canShiftForward = monthIndex(value.monthAnchor) < monthIndex(new Date());

  const shiftMonth = (offset: number) => {
    const anchor = new Date(value.monthAnchor.getFullYear(), value.monthAnchor.getMonth() + offset, 1);
    if (monthIndex(anchor) > monthIndex(new Date())) {
      return;
    }
    onChange({ preset: 'month', monthAnchor: anchor });
  };

  const monthLabel = value.monthAnchor.toLocaleDateString(i18n.language, {
    month: 'long',
    year: 'numeric',
  });

  const dates = analyticsRangeToDates(value);

  return (
    <div className="mb-16 flex items-end flex-wrap gap-x-16 gap-y-8">
      <TabBar
        key={`${value.preset}-${visiblePresets.length}`}
        tabs={tabs}
        defaultIndex={visiblePresets.findIndex(preset => preset.key === value.preset)}
      />
      <div className="flex items-center gap-x-4 pb-15">
        <button
          type="button"
          className="flex items-center justify-center p-4 rounded-xs cursor-pointer text-faded hover:text-full hover:surface-elevated-more transition-colors"
          aria-label={t('carrier_analytics.previous_month')}
          onClick={() => shiftMonth(-1)}>
          <Icon name="chevronLeft" width={18} height={18} />
        </button>
        <span className="typo-text-14--medium text-full min-w-[96px] text-center">{monthLabel}</span>
        <button
          type="button"
          className={classNames(
            'flex items-center justify-center p-4 rounded-xs',
            canShiftForward
              ? 'cursor-pointer text-faded hover:text-full hover:surface-elevated-more transition-colors'
              : 'text-disabled',
          )}
          disabled={!canShiftForward}
          aria-label={t('carrier_analytics.next_month')}
          onClick={() => shiftMonth(1)}>
          <Icon name="chevronRight" width={18} height={18} />
        </button>
      </div>
      <Tooltip title={t('carrier_analytics.data_hint')} placement="top-end">
        <span className="typo-text-12 text-faded ml-auto pb-20 flex items-center gap-x-4">
          <Icon name="infoOutlined" width={14} height={14} />
          {dates.date_from ? `${formatDisplayDate(dates.date_from)} – ` : `${t('carrier_analytics.preset_total')} – `}
          {formatDisplayDate(dates.date_to)}
        </span>
      </Tooltip>
    </div>
  );
};

export default CarrierAnalyticsToolbar;
